import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Background from "@/components/landing/Background";
import AppHeader from "@/components/nav/AppHeader";
import { Mail, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";

/**
 * ForgotPassword page — request a password reset email.
 *
 * URL: /forgot-password
 *
 * Anti-enumeration: the backend always returns 200 regardless of whether
 * the email exists, so we always show the same "if account exists, an
 * email was sent" success state. This prevents attackers from probing
 * the user database.
 *
 * The user can submit multiple times (e.g. resend) — the backend rate
 * limit handles abuse (3/hour per IP via PASSWORD_RESET bucket).
 */
const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !email.trim()) return;
        setError("");
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
        } catch (err) {
            // Backend should be enumeration-safe. If we land here it's a
            // rate-limit (429) or server error.
            if (err?.response?.status === 429) {
                setError("Забагато спроб. Зачекай 15 хвилин і спробуй знову.");
            } else {
                setError("Щось пішло не так. Спробуй пізніше або напиши в підтримку.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-bg text-default-c relative overflow-hidden">
            <Background />
            <AppHeader />

            <main className="relative z-10 max-w-md mx-auto px-6 pt-24 pb-12">
                <div className="bg-surface/80 backdrop-blur-md border border-border rounded-2xl p-8 shadow-xl">
                    {!sent ? (
                        <>
                            <div className="text-center mb-6">
                                <Mail className="mx-auto text-primary mb-3" size={36} />
                                <h1 className="text-2xl font-bold mb-2">Забули пароль?</h1>
                                <p className="text-sm text-muted-c">
                                    Введи email від акаунту — надішлемо лист зі скиданням паролю.
                                </p>
                            </div>

                            <form
                                onSubmit={handleSubmit}
                                data-testid="forgot-password-form"
                                className="space-y-4"
                            >
                                <div>
                                    <label className="block text-xs text-muted-c mb-1.5">Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        autoFocus
                                        required
                                        data-testid="forgot-email-input"
                                        className="w-full h-10 px-3 rounded-md bg-bg border border-border text-sm focus:outline-none focus:border-primary transition"
                                    />
                                </div>

                                {error && (
                                    <div
                                        data-testid="forgot-error"
                                        className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-md p-3"
                                    >
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting || !email.trim()}
                                    data-testid="forgot-submit"
                                    className="w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2"
                                >
                                    {submitting && <Loader2 className="animate-spin" size={16} />}
                                    Надіслати інструкції
                                </button>
                            </form>
                        </>
                    ) : (
                        <div data-testid="forgot-success" className="text-center">
                            <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={40} />
                            <h1 className="text-2xl font-bold mb-2">Перевір пошту</h1>
                            <p className="text-sm text-muted-c mb-2">
                                Якщо акаунт з цим email існує, ми надіслали лист з посиланням для скидання паролю.
                            </p>
                            <p className="text-xs text-faint-c mb-6">
                                Посилання діє 1 годину. Перевір папку «Спам», якщо не бачиш листа.
                            </p>
                            <button
                                type="button"
                                onClick={() => { setSent(false); setEmail(""); }}
                                data-testid="forgot-resend"
                                className="text-xs text-muted-c hover:text-primary transition"
                            >
                                Не отримав лист? Спробуй ще раз
                            </button>
                        </div>
                    )}

                    <div className="mt-6 pt-6 border-t border-border text-center">
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-1 text-xs text-muted-c hover:text-primary transition"
                        >
                            <ArrowLeft size={12} />
                            Назад до входу
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ForgotPassword;
